/**
 * PeriodManager - Manage accounting periods (fiscal months/quarters)
 * Close/reopen periods and auto-generate for a fiscal year.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { AccountingPeriod } from '../../../types/accounting';

interface PeriodManagerProps {
  businessId: number;
}

type ModalView = 'none' | 'generate' | 'add';

const API_BASE = process.env.REACT_APP_API_URL || '';

const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

const PeriodManager: React.FC<PeriodManagerProps> = ({ businessId }) => {
  const [periods, setPeriods] = useState<AccountingPeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [modalView, setModalView] = useState<ModalView>('none');
  const [actionLoading, setActionLoading] = useState(false);

  // Confirmation dialog state
  const [confirmAction, setConfirmAction] = useState<{
    periodId: number;
    periodName: string;
    action: 'close' | 'reopen';
  } | null>(null);

  // Generate form state
  const [generateYear, setGenerateYear] = useState(new Date().getFullYear());
  const [generateFrequency, setGenerateFrequency] = useState<'monthly' | 'quarterly'>('monthly');

  // Add period form state
  const [addForm, setAddForm] = useState({
    period_name: '',
    start_date: '',
    end_date: ''
  });

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 4000);
  };

  const loadPeriods = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(
        `${API_BASE}/api/accounting/periods/business/${businessId}`,
        { headers: getAuthHeaders() }
      );

      // Guard against non-JSON responses (e.g. HTML error pages)
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('Server returned an unexpected response (status ' + response.status + '). Please try again.');
      }

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to load periods');
      }

      setPeriods(result.periods || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load periods');
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    loadPeriods();
  }, [loadPeriods]);

  const handleClosePeriod = async (periodId: number) => {
    try {
      setActionLoading(true);
      setError(null);
      const response = await fetch(
        `${API_BASE}/api/accounting/periods/business/${businessId}/${periodId}/close`,
        { method: 'POST', headers: getAuthHeaders() }
      );

      // Guard against non-JSON responses (e.g. HTML error pages)
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('Server returned an unexpected response (status ' + response.status + ')');
      }

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to close period');
      }

      showSuccess('Period closed successfully');
      setConfirmAction(null);
      loadPeriods();
    } catch (err: any) {
      setError(err.message || 'Failed to close period');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReopenPeriod = async (periodId: number) => {
    try {
      setActionLoading(true);
      setError(null);
      const response = await fetch(
        `${API_BASE}/api/accounting/periods/business/${businessId}/${periodId}/reopen`,
        { method: 'POST', headers: getAuthHeaders() }
      );

      // Guard against non-JSON responses (e.g. HTML error pages)
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('Server returned an unexpected response (status ' + response.status + ')');
      }

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to reopen period');
      }

      showSuccess('Period reopened successfully');
      setConfirmAction(null);
      loadPeriods();
    } catch (err: any) {
      setError(err.message || 'Failed to reopen period');
    } finally {
      setActionLoading(false);
    }
  };

  const handleGenerate = async () => {
    try {
      setActionLoading(true);
      setError(null);
      const response = await fetch(
        `${API_BASE}/api/accounting/periods/business/${businessId}/generate`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ frequency: generateFrequency, year: generateYear })
        }
      );

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('Server returned an unexpected response (status ' + response.status + ')');
      }

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to generate periods');
      }

      showSuccess(result.message || 'Periods generated');
      setModalView('none');
      loadPeriods();
    } catch (err: any) {
      setError(err.message || 'Failed to generate periods');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddPeriod = async () => {
    if (!addForm.period_name || !addForm.start_date || !addForm.end_date) {
      setError('All fields are required');
      return;
    }

    try {
      setActionLoading(true);
      setError(null);
      const response = await fetch(
        `${API_BASE}/api/accounting/periods/business/${businessId}`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(addForm)
        }
      );

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('Server returned an unexpected response (status ' + response.status + ')');
      }

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to create period');
      }

      showSuccess(`Period "${addForm.period_name}" created`);
      setAddForm({ period_name: '', start_date: '', end_date: '' });
      setModalView('none');
      loadPeriods();
    } catch (err: any) {
      setError(err.message || 'Failed to create period');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr: string): string => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // --- Styles ---

  const styles = {
    container: {
      padding: '24px',
      maxWidth: '900px',
    } as React.CSSProperties,
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
    } as React.CSSProperties,
    title: {
      fontSize: '20px',
      fontWeight: 600,
      margin: 0,
      color: '#1f2937',
    } as React.CSSProperties,
    headerButtons: {
      display: 'flex',
      gap: '8px',
    } as React.CSSProperties,
    btnPrimary: {
      padding: '8px 16px',
      backgroundColor: '#059669',
      color: '#fff',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 500,
    } as React.CSSProperties,
    btnSecondary: {
      padding: '8px 16px',
      backgroundColor: '#f3f4f6',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 500,
    } as React.CSSProperties,
    btnDanger: {
      padding: '6px 12px',
      backgroundColor: '#dc2626',
      color: '#fff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '13px',
    } as React.CSSProperties,
    btnSmall: {
      padding: '6px 12px',
      backgroundColor: '#059669',
      color: '#fff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '13px',
    } as React.CSSProperties,
    btnCancel: {
      padding: '8px 16px',
      backgroundColor: '#fff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px',
    } as React.CSSProperties,
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const,
      fontSize: '14px',
    },
    th: {
      textAlign: 'left' as const,
      padding: '10px 12px',
      borderBottom: '2px solid #e5e7eb',
      color: '#6b7280',
      fontWeight: 600,
      fontSize: '13px',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.05em',
    },
    td: {
      padding: '10px 12px',
      borderBottom: '1px solid #f3f4f6',
      color: '#1f2937',
    },
    badgeOpen: {
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 600,
      backgroundColor: '#d1fae5',
      color: '#059669',
    } as React.CSSProperties,
    badgeClosed: {
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 600,
      backgroundColor: '#f3f4f6',
      color: '#6b7280',
    } as React.CSSProperties,
    badgeLocked: {
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 600,
      backgroundColor: '#fee2e2',
      color: '#dc2626',
    } as React.CSSProperties,
    // errorBox removed - using shared .error-message class from shared.css
    successBox: {
      padding: '12px 16px',
      backgroundColor: '#ecfdf5',
      color: '#059669',
      borderRadius: '6px',
      marginBottom: '16px',
      fontSize: '14px',
    } as React.CSSProperties,
    overlay: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.4)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    modal: {
      backgroundColor: '#fff',
      borderRadius: '8px',
      padding: '24px',
      width: '420px',
      maxWidth: '90vw',
      boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
    } as React.CSSProperties,
    modalTitle: {
      fontSize: '18px',
      fontWeight: 600,
      marginBottom: '16px',
      color: '#1f2937',
    } as React.CSSProperties,
    formGroup: {
      marginBottom: '14px',
    } as React.CSSProperties,
    label: {
      display: 'block',
      fontSize: '13px',
      fontWeight: 500,
      color: '#374151',
      marginBottom: '4px',
    } as React.CSSProperties,
    input: {
      width: '100%',
      padding: '8px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      boxSizing: 'border-box' as const,
    },
    select: {
      width: '100%',
      padding: '8px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      backgroundColor: '#fff',
      boxSizing: 'border-box' as const,
    },
    modalFooter: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '8px',
      marginTop: '20px',
    } as React.CSSProperties,
    emptyState: {
      textAlign: 'center' as const,
      padding: '40px 20px',
      color: '#6b7280',
      fontSize: '14px',
    },
    loadingText: {
      textAlign: 'center' as const,
      padding: '40px 20px',
      color: '#6b7280',
      fontSize: '14px',
    },
    confirmText: {
      fontSize: '14px',
      color: '#374151',
      lineHeight: 1.6,
      marginBottom: '8px',
    } as React.CSSProperties,
  };

  const getStatusBadge = (status: string) => {
    if (status === 'open') return <span style={styles.badgeOpen}>Open</span>;
    if (status === 'locked') return <span style={styles.badgeLocked}>Locked</span>;
    return <span style={styles.badgeClosed}>Closed</span>;
  };

  // --- Render ---

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h2 style={styles.title}>Accounting Periods</h2>
        <div style={styles.headerButtons}>
          <button style={styles.btnSecondary} onClick={() => setModalView('add')}>
            Add Period
          </button>
          <button style={styles.btnPrimary} onClick={() => setModalView('generate')}>
            Generate Periods
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="error-message" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '16px' }}
          >
            x
          </button>
        </div>
      )}

      {successMessage && (
        <div style={styles.successBox}>{successMessage}</div>
      )}

      {/* Table */}
      {loading ? (
        <div style={styles.loadingText}>Loading periods...</div>
      ) : periods.length === 0 ? (
        <div style={styles.emptyState}>
          <p>No accounting periods yet.</p>
          <p>Click "Generate Periods" to auto-create monthly or quarterly periods for your fiscal year.</p>
        </div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Period Name</th>
              <th style={styles.th}>Start Date</th>
              <th style={styles.th}>End Date</th>
              <th style={styles.th}>Status</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {periods.map((period) => (
              <tr key={period.id}>
                <td style={styles.td}>{period.period_name}</td>
                <td style={styles.td}>{formatDate(period.start_date)}</td>
                <td style={styles.td}>{formatDate(period.end_date)}</td>
                <td style={styles.td}>{getStatusBadge(period.status)}</td>
                <td style={{ ...styles.td, textAlign: 'right' }}>
                  {period.status === 'open' && (
                    <button
                      style={styles.btnDanger}
                      onClick={() => setConfirmAction({
                        periodId: period.id,
                        periodName: period.period_name,
                        action: 'close'
                      })}
                    >
                      Close
                    </button>
                  )}
                  {period.status === 'closed' && (
                    <button
                      style={styles.btnSmall}
                      onClick={() => setConfirmAction({
                        periodId: period.id,
                        periodName: period.period_name,
                        action: 'reopen'
                      })}
                    >
                      Reopen
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Confirmation Dialog */}
      {confirmAction && (
        <div style={styles.overlay} onClick={() => !actionLoading && setConfirmAction(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>
              {confirmAction.action === 'close' ? 'Close Period' : 'Reopen Period'}
            </h3>
            <p style={styles.confirmText}>
              {confirmAction.action === 'close'
                ? `Are you sure you want to close "${confirmAction.periodName}"? No new journal entries can be posted to a closed period.`
                : `Are you sure you want to reopen "${confirmAction.periodName}"? This will allow new journal entries in this date range.`
              }
            </p>
            <div style={styles.modalFooter}>
              <button
                style={styles.btnCancel}
                onClick={() => setConfirmAction(null)}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                style={confirmAction.action === 'close' ? styles.btnDanger : styles.btnPrimary}
                onClick={() => {
                  if (confirmAction.action === 'close') {
                    handleClosePeriod(confirmAction.periodId);
                  } else {
                    handleReopenPeriod(confirmAction.periodId);
                  }
                }}
                disabled={actionLoading}
              >
                {actionLoading
                  ? 'Processing...'
                  : confirmAction.action === 'close' ? 'Close Period' : 'Reopen Period'
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generate Periods Modal */}
      {modalView === 'generate' && (
        <div style={styles.overlay} onClick={() => !actionLoading && setModalView('none')}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Generate Periods</h3>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
              Auto-create periods for a fiscal year. Existing periods will be skipped.
            </p>
            <div style={styles.formGroup}>
              <label style={styles.label}>Year</label>
              <input
                type="number"
                style={styles.input}
                value={generateYear}
                onChange={(e) => setGenerateYear(parseInt(e.target.value) || new Date().getFullYear())}
                min={2020}
                max={2040}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Frequency</label>
              <select
                style={styles.select}
                value={generateFrequency}
                onChange={(e) => setGenerateFrequency(e.target.value as 'monthly' | 'quarterly')}
              >
                <option value="monthly">Monthly (12 periods)</option>
                <option value="quarterly">Quarterly (4 periods)</option>
              </select>
            </div>
            <div style={styles.modalFooter}>
              <button
                style={styles.btnCancel}
                onClick={() => setModalView('none')}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                style={styles.btnPrimary}
                onClick={handleGenerate}
                disabled={actionLoading}
              >
                {actionLoading ? 'Generating...' : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Period Modal */}
      {modalView === 'add' && (
        <div style={styles.overlay} onClick={() => !actionLoading && setModalView('none')}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Add Period</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Period Name</label>
              <input
                type="text"
                style={styles.input}
                placeholder="e.g. January 2026"
                value={addForm.period_name}
                onChange={(e) => setAddForm({ ...addForm, period_name: e.target.value })}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Start Date</label>
              <input
                type="date"
                style={styles.input}
                value={addForm.start_date}
                onChange={(e) => setAddForm({ ...addForm, start_date: e.target.value })}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>End Date</label>
              <input
                type="date"
                style={styles.input}
                value={addForm.end_date}
                onChange={(e) => setAddForm({ ...addForm, end_date: e.target.value })}
              />
            </div>
            <div style={styles.modalFooter}>
              <button
                style={styles.btnCancel}
                onClick={() => {
                  setAddForm({ period_name: '', start_date: '', end_date: '' });
                  setModalView('none');
                }}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                style={styles.btnPrimary}
                onClick={handleAddPeriod}
                disabled={actionLoading}
              >
                {actionLoading ? 'Creating...' : 'Create Period'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PeriodManager;
